import { useState, useEffect, useCallback } from "react";
import { getToken } from "../api/auth";
import { API_ENDPOINTS } from "../api/apiConfig";
import { filterSitesForUser, isAdminUser } from "./siteAccessUtils";
import "../styles/siteSelector.css";

const SITES_PER_PAGE = 8;

// Accent colors cycling for card left-border
const ACCENT_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#d97706", "#16a34a", "#dc2626", "#9333ea", "#0284c7"];

export default function SiteSelector({ onSelectSite, onManageSite, userEmail, userType, authUser, onLogout }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [filteredSites, setFilteredSites] = useState([]);
  const isAdmin = isAdminUser(userType);

  const filterSites = useCallback((siteArray) => {
    return filterSitesForUser(siteArray, userType, authUser);
  }, [authUser, userType]);

  useEffect(() => {
    console.log("=== SiteSelector Mounted ===");
    console.log("userEmail:", userEmail);
  }, [userEmail]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoading(true);
        console.log("SiteSelector: Fetching sites from /api/sites");
        const token = getToken();
        console.log("SiteSelector: Token:", token ? "exists" : "missing");
        const res = await fetch(`${API_ENDPOINTS.sites.list}`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        console.log("SiteSelector: Response status:", res.status);
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        console.log("SiteSelector: Data received:", data);
        // Filter only active sites based on status field - handle different response formats
        let dataArray = Array.isArray(data) ? data : data.sites ? data.sites : (data.id ? [data] : []);
        const activeSites = dataArray.filter(
          (s) => s.status === "active" || s.active === true
        );
        const authorizedSites = filterSites(activeSites);
        console.log("SiteSelector: Active sites:", authorizedSites);
        setSites(authorizedSites);
        setFilteredSites(authorizedSites);
      } catch (err) {
        console.error("SiteSelector: Error fetching sites:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSites();
  }, [filterSites]);

  // Clear search filter
  const handleClear = () => {
    setSearch("");
    setPage(1);
    setFilteredSites(sites);
  };

  // Search sites API call
  const handleSearch = async (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setPage(1);

    if (searchValue.trim() === "") {
      // If search is empty, show all sites
      setFilteredSites(sites);
      return;
    }

    try {
      const token = getToken();
      const url = `${API_ENDPOINTS.sites.search}?name=${encodeURIComponent(searchValue)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();
        // Handle different response formats: single object, array, or object with sites property
        let dataArray = Array.isArray(data) ? data : data.sites ? data.sites : (data.id ? [data] : []);
        const searchResults = dataArray.filter(
          (s) => s.status === "active" || s.active === true
        );
        setFilteredSites(filterSites(searchResults));
      } else {
        console.error("Search error:", res.status);
      }
    } catch (err) {
      console.error("Error searching sites:", err);
    }
  };

  const filtered = filteredSites;

  const totalPages = Math.max(1, Math.ceil(filtered.length / SITES_PER_PAGE));
  const paginated = filtered.slice((page - 1) * SITES_PER_PAGE, page * SITES_PER_PAGE);

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logo}>
            <div style={styles.logoImage}>
              <img src="/unnamed.png" alt="SKT Infracon Tracker" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={styles.logoText}>SKT Infracon Tracker</span>
          </div>
          <div style={styles.headerRight}>
            <span style={styles.username}>{userEmail}</span>
            <button
              onClick={onLogout}
              style={styles.logoutBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.9)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#ef4444";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>Select Active Site</h1>
        <p style={styles.heroSub}>Choose a construction site to continue</p>
      </section>

      {/* ── Content ── */}
      <main style={styles.main}>
        {/* Top bar */}
        <div style={styles.topBar}>
          <div>
            <div style={styles.activeSitesTitle}>
              <span style={styles.siteIcon}>🏗</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Active Sites</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Click on any site to open its dashboard</div>
              </div>
            </div>
          </div>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              style={styles.searchInput}
              placeholder="Search sites..."
              value={search}
              onChange={handleSearch}
            />
            {search && (
              <button
                onClick={handleClear}
                style={styles.clearBtn}
                title="Clear search filter"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ef4444";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* States */}
        {loading && <LoadingGrid />}
        {error && <ErrorState message={error} />}
        {!loading && !error && filtered.length === 0 && <EmptyState />}

        {/* Grid */}
        {!loading && !error && paginated.length > 0 && (
          <div style={styles.grid}>
            {paginated.map((site, i) => (
              <SiteCard
                key={site.id || site.site_name + i}
                site={site}
                accent={ACCENT_COLORS[i % ACCENT_COLORS.length]}
                onOpen={() => onSelectSite && onSelectSite(site)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination current={page} total={totalPages} onChange={setPage} />
        )}
      </main>
    </div>
  );
}

/* ─── Sub-components ─── */

function SiteCard({ site, accent, onOpen }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.card,
        borderLeft: `4px solid ${accent}`,
        boxShadow: hovered
          ? "0 8px 32px rgba(37,99,235,.18)"
          : "0 2px 10px rgba(0,0,0,.07)",
        transform: hovered ? "translateY(-3px)" : "none",
        transition: "box-shadow .2s, transform .2s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.cardTop}>
        <div style={{ flex: 1 }}>
          <div style={styles.cardName}>{site.site_name || site.siteName}</div>
          <div style={styles.cardLocation}>📍 {site.location}</div>
        </div>
        <div
          style={{
            ...styles.cardImg,
            background: `linear-gradient(135deg, ${accent}22, ${accent}44)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
          }}
        >
          🏗️
        </div>
      </div>

      <div style={styles.cardMeta}>
        <MetaBadge icon="👷" label="Status" value="Active" green />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button
          style={{ ...styles.openBtn, background: accent }}
          onClick={onOpen}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = ".88")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          Open Site →
        </button>
      </div>
    </div>
  );
}

function MetaBadge({ icon, label, value, green }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
      <span>{icon}</span>
      <span>{label}</span>
      {green ? (
        <span style={styles.greenBadge}>{value}</span>
      ) : (
        <span style={{ fontWeight: 600, color: "#111827" }}>{value}</span>
      )}
    </div>
  );
}

function Pagination({ current, total, onChange }) {
  const pages = Array.from({ length: total }, (_, i) => i + 1);
  return (
    <div style={styles.pagination}>
      <button style={styles.pgBtn} onClick={() => onChange(Math.max(1, current - 1))} disabled={current === 1}>‹</button>
      {pages.map((p) => (
        <button
          key={p}
          style={{ ...styles.pgBtn, ...(p === current ? styles.pgActive : {}) }}
          onClick={() => onChange(p)}
        >
          {p}
        </button>
      ))}
      <button style={styles.pgBtn} onClick={() => onChange(Math.min(total, current + 1))} disabled={current === total}>›</button>
    </div>
  );
}

function LoadingGrid() {
  return (
    <div style={styles.grid}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{ ...styles.card, borderLeft: "4px solid #e5e7eb" }}>
          <div style={{ height: 20, background: "#f3f4f6", borderRadius: 6, marginBottom: 10, width: "70%", animation: "pulse 1.4s infinite" }} />
          <div style={{ height: 14, background: "#f3f4f6", borderRadius: 6, marginBottom: 24, width: "50%" }} />
          <div style={{ height: 36, background: "#f3f4f6", borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#dc2626" }}>
      <div style={{ fontSize: 40 }}>⚠️</div>
      <div style={{ fontWeight: 600, marginTop: 12 }}>Failed to load sites</div>
      <div style={{ fontSize: 14, color: "#6b7280", marginTop: 6 }}>{message}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
      <div style={{ fontSize: 40 }}>🏗️</div>
      <div style={{ fontWeight: 600, marginTop: 12, color: "#374151" }}>No active sites found</div>
      <div style={{ fontSize: 14, marginTop: 6 }}>Try adjusting your search or check back later.</div>
    </div>
  );
}

/* ─── Styles ─── */
const styles = {
  page: { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Segoe UI', system-ui, sans-serif" },

  // Match Dashboard header color scheme
  header: { background: "linear-gradient(90deg, #0f172a 0%, #1e3a8a 60%, #7c3aed 100%)", padding: "0 28px", height: 58 },
  headerInner: { maxWidth: 1200, margin: "0 auto", height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" },
  logo: { display: "flex", alignItems: "center", gap: 12 },
  logoImage: { width: 36, height: 36, borderRadius: 9, overflow: "hidden", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: "0.5px" },
  headerRight: { display: "flex", alignItems: "center", gap: 14 },
  username: { color: "#cbd5e1", fontSize: 14, fontWeight: 500 },
  logoutBtn: {
    background: "#ef4444",
    border: "none",
    color: "#fff",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background .2s, transform .2s",
  },

  hero: {
    background: "linear-gradient(90deg, #0f172a 0%, #1e3a8a 60%, #7c3aed 100%)",
    padding: "48px 24px 56px",
    textAlign: "center",
  },
  heroTitle: { color: "#fff", fontSize: "clamp(22px,4vw,32px)", fontWeight: 800, margin: "0 0 8px" },
  heroSub: { color: "rgba(255,255,255,.65)", fontSize: 15, margin: 0 },

  main: { maxWidth: 1200, margin: "-20px auto 0", padding: "0 20px 60px", position: "relative", zIndex: 1 },

  topBar: {
    background: "#fff",
    borderRadius: 14,
    padding: "18px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    boxShadow: "0 2px 10px rgba(0,0,0,.07)",
    flexWrap: "wrap",
    gap: 12,
  },
  activeSitesTitle: { display: "flex", alignItems: "center", gap: 12 },
  siteIcon: { fontSize: 28, background: "#f1f5f9", padding: 8, borderRadius: 10 },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 14px" },
  searchIcon: { fontSize: 14, color: "#9ca3af" },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 14, color: "#374151", width: 200 },
  clearBtn: { background: "transparent", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", padding: "0 4px", transition: "all .2s", fontWeight: 600 },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 20, marginBottom: 32 },

  card: {
    background: "#fff",
    borderRadius: 14,
    padding: "22px 20px 18px",
    cursor: "pointer",
  },
  cardTop: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 },
  cardImg: { width: 64, height: 64, borderRadius: 10, flexShrink: 0, overflow: "hidden" },
  cardName: { fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 4, lineHeight: 1.3 },
  cardLocation: { fontSize: 13, color: "#6b7280" },
  cardMeta: { display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" },
  greenBadge: { background: "#dcfce7", color: "#16a34a", fontWeight: 600, fontSize: 11, padding: "2px 8px", borderRadius: 20 },
  openBtn: {
    width: "100%",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 0",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: ".02em",
  },

  pagination: { display: "flex", justifyContent: "center", gap: 6, marginTop: 8 },
  pgBtn: { width: 36, height: 36, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 14, color: "#374151" },
  pgActive: { background: "#2563eb", color: "#fff", border: "1px solid #2563eb", fontWeight: 700 },
};
